import React from 'react';
import { MessageSquare, Send, User, HelpCircle } from 'lucide-react';

export interface Question {
  id: string;
  user: string;
  text: string;
  timestamp: string;
  replies?: Question[];
}

interface QuestionSectionProps {
  postId: string;
  questions: Question[];
  onAddQuestion: (postId: string, text: string) => void;
}

export function QuestionSection({ postId, questions, onAddQuestion }: QuestionSectionProps) {
  const [text, setText] = React.useState('');
  const [showQuestions, setShowQuestions] = React.useState(questions.length > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim()) {
      onAddQuestion(postId, text);
      setText('');
      setShowQuestions(true); // Auto-expand after adding comment
    }
  };

  return (
    <div className="mt-2 pt-6 border-t-2 border-zinc-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-black text-zinc-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-green-600" />
          Community Comments ({questions.length})
        </h3>
        <button
          onClick={() => setShowQuestions(!showQuestions)}
          className="text-sm font-bold text-green-600 hover:text-green-700 transition-colors px-3 py-1.5 rounded-lg hover:bg-green-50"
        >
          {showQuestions ? 'Hide' : 'Show'}
        </button>
      </div>

      {showQuestions && (
        <div className="space-y-4">
          {/* Help Tip */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
            <HelpCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              <strong>💬 Chat with neighbors:</strong> Ask questions or share updates about this report
            </p>
          </div>

          {/* Comment Input */}
          <form onSubmit={handleSubmit} className="relative">
            <input
              type="text"
              placeholder="💬 Type your comment here..."
              className="w-full bg-white border-2 border-green-200 rounded-xl py-3 pl-4 pr-12 text-sm focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-hidden transition-all placeholder:text-zinc-400"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button
              type="submit"
              disabled={!text.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          {/* Comments List */}
          {questions.length > 0 ? (
            <div className="space-y-3">
              {questions.map((q) => (
                <div key={q.id} className="bg-zinc-50 p-4 rounded-xl border border-zinc-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-sm">
                        {q.user[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm font-bold text-zinc-900">{q.user}</span>
                    </div>
                    <span className="text-xs font-medium text-zinc-400">{q.timestamp}</span>
                  </div>
                  <p className="text-sm text-zinc-700 leading-relaxed ml-10">{q.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 text-zinc-300" />
              <p className="text-sm font-medium">No comments yet</p>
              <p className="text-xs">Be the first to comment!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}